import PropTypes from 'prop-types';
import cx from 'classnames';

import isNotEmptyReactElementContent from 'core/utils/content';
import { resolveScopedStyles } from 'core/utils/styled-jsx';

import withBreakpoint from 'core/hocs/withBreakpoint';

import H3 from '../H3';
import { Block, Section } from '../Grid';

import styles from './index.styl';


/**
 * @author Evgeny Shiryaev <e.shiryaev@rambler-co.ru>
 */
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children, grid, isMobile, columns }) => grid || isMobile || columns < 2
  ? <Section>{children}</Section>
  : <div className={styles.columns} style={{ columnCount: columns }}>{children}</div>;


function Feed(props) {
  const {
    grid,
    title,
    content,
    columns,
    isMobile,
    card: BaseCard,
    cardsReplacement,
    interitemSpacing,
    heroCard: HeroCard,
    headerComponent: Header,
    dataQa,
  } = props;

  if (!content?.length) return null;

  const replacements = cardsReplacement || {};
  const halfInteritemSpacing = interitemSpacing / 2;

  const scope = interitemSpacing
    ? resolveScopedStyles(
      <scope>
        <style jsx>{`
          .header
            margin-bottom ${interitemSpacing}px

          .item
            padding ${halfInteritemSpacing}px

            :global(.mobile) &
              padding ${halfInteritemSpacing}px 0

          .container
            margin ${-halfInteritemSpacing}px

            :global(.mobile) &
              margin ${-halfInteritemSpacing}px 0
        `}</style>
      </scope>
    ) : null;

  return (
    <div {...dataQa && { 'data-qa': dataQa }}>
      {isNotEmptyReactElementContent(title) && (
        <Header
          className={scope && scope.wrapClassNames('header')}
          {...dataQa && { 'data-qa': dataQa + '-title' }}
        >
          {title}
        </Header>
      )}
      <div
        className={scope && scope.wrapClassNames('container')}
        {...dataQa && { 'data-qa': dataQa + '-cards' }}
      >
        <Wrapper
          grid={grid}
          columns={columns}
          isMobile={isMobile}
        >
          {content.map((topic, i) => {
            const cardProps = {
              position: i,
              content: topic,
            };

            const Replacement = replacements[i];
            const Card = HeroCard && !i ? HeroCard : BaseCard;

            const card = <Card {...cardProps} />;

            const item = Replacement
              ? <Replacement {...cardProps} card={card} />
              : card;

            return (
              <Block
                width={12 / (grid ? columns : 1)}
                mobile={12}
                key={topic.id}
                className={cx(scope && scope.wrapClassNames('item'), styles.item)}
              >
                {item}
              </Block>
            );
          })}
        </Wrapper>
      </div>
      {scope && <scope.styles />}
    </div>
  );
}

Feed.propTypes = {
  /** @ignore */
  isMobile: PropTypes.bool,
  /** Заголовок блока. */
  title: PropTypes.node,
  /** Компонент, в который будет завёрнут заголовок блока. */
  headerComponent: PropTypes.func,
  /** Карточка, с помощью которой будут отрисовываться топики. */
  card: PropTypes.func.isRequired,
  /** Карточка, с помощью которой будет отрисовываться первый топик. */
  heroCard: PropTypes.func,
  /**
   * Количество колонок, в которых будут выводиться карточки.
   * На брейкпойнте `MOBILE` карточки всегда выводятся в одну колонку.
   */
  columns: PropTypes.number.isRequired,

  /**
   * Данные о топиках.
   */
  content: PropTypes.array,

  /**
   * Параметр отвечает за отображение карточек в гриде.
   * - И (в столбец) `grid: false`
   * - Z (в строку) `grid: true`
   */
  grid: PropTypes.bool.isRequired,

  /**
   * Расстояние между карточками (горизонтальное + вертикальное).
   */
  interitemSpacing: PropTypes.number.isRequired,

  /**
   * Справочник замен карточек на другие компоненты.
   *
   * В качестве ключа должен выступать индекс карточки которую нужно заменить,
   * а в качестве значения компонент на который будет заменена карточка.
   *
   * Элемент отрисованной карточки с данными будет передан в новый компонент
   * в качестве пропса `card`.
   */
  cardsReplacement: PropTypes.objectOf(PropTypes.func.isRequired),
  /** @ignore */
  dataQa: PropTypes.string,
};

Feed.defaultProps = {
  grid: true,
  columns: 1,
  interitemSpacing: 0,
  headerComponent: H3,
};

const FeedComponent = withBreakpoint(Feed);

FeedComponent.displayName = 'Feed';

export { Feed as StorybookComponent };
export default FeedComponent;
